import { useState } from "react";

import { CalenderIcon } from "../../Icons";
import {
  type DateRangePreset,
  MobileDateRangeSelector,
} from "../../MobileDateRange/MobileDateRangeSelector";
import { Typo } from "../../Typo";
import { usePerformanceReportTranslations } from "../hooks/usePerformanceReportTranslations";
import { usePerformanceReportContext } from "../providers/PerformanceReportProvider";
import {
  clampPerformanceReportRange,
  PERFORMANCE_REPORT_MAX_RANGE_DAYS,
  PERFORMANCE_REPORT_MIN_DATE,
} from "../urlParams/schema";

/**
 * Preset ranges for the date shortcut sheet. Built fresh (plain `Date` math,
 * matching the provider's `defaultRange`) so ranges are correct at open time,
 * with labels/periods mirroring the desktop `Datepicker` shortcuts 1:1 so the
 * two breakpoints stay consistent. The default "Last 30 days" range therefore
 * matches this list's `last30` entry and pre-selects on open.
 */
const buildDatePresets = (
  labels: ReturnType<typeof usePerformanceReportTranslations>,
): DateRangePreset[] => {
  const today = new Date();
  const daysAgo = (n: number) => {
    const d = new Date(today);
    d.setDate(today.getDate() - n);
    return d;
  };
  const firstOfMonth = (offset: number) =>
    new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const lastOfMonth = (offset: number) =>
    new Date(today.getFullYear(), today.getMonth() + offset + 1, 0);

  const presets: DateRangePreset[] = [
    {
      key: "today",
      label: labels.datepickerToday,
      range: { from: today, to: today },
    },
    {
      key: "yesterday",
      label: labels.datepickerYesterday,
      range: { from: daysAgo(1), to: daysAgo(1) },
    },
    {
      key: "last7",
      label: labels.datepickerPast(7),
      range: { from: daysAgo(7), to: today },
    },
    {
      key: "last30",
      label: labels.datepickerPast(30),
      range: { from: daysAgo(30), to: today },
    },
    {
      key: "thisMonth",
      label: labels.datepickerCurrentMonth,
      range: { from: firstOfMonth(0), to: lastOfMonth(0) },
    },
    {
      key: "lastMonth",
      label: labels.datepickerPastMonth,
      range: { from: firstOfMonth(-1), to: lastOfMonth(-1) },
    },
  ];

  return presets.map((preset) => ({
    ...preset,
    range: clampPerformanceReportRange(preset.range),
  }));
};

const isSameDay = (a?: Date | null, b?: Date | null) =>
  !!a &&
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Returns the label of the preset whose range matches the applied date range,
 * or `undefined` when the range is custom. Mirrors the desktop `Datepicker`
 * field variant, which shows the matched shortcut label (e.g. "Last 30 days")
 * instead of the formatted range when the selection came from a shortcut.
 */
const getMatchedPresetLabel = (
  presets: DateRangePreset[],
  startDate?: Date | null,
  endDate?: Date | null,
): string | undefined =>
  presets.find(
    (preset) =>
      isSameDay(preset.range.from, startDate) &&
      isSameDay(preset.range.to, endDate),
  )?.label;

/**
 * Mobile date-range picker: a tonal date pill (button) that opens the two-step
 * `MobileDateRangeSelector` (shortcut presets → custom picker). Self-contained
 * — owns its own open state and the preset list — mirroring how desktop's
 * `Datepicker` is self-contained in `PerformanceControls`.
 *
 * When the applied range matches a preset it shows that label (e.g. "Last 30
 * days") instead of the formatted range, mirroring the desktop field-variant
 * `Datepicker`.
 *
 * Fill/color/size match the canvas's `.m-datepick` exactly (tonal date pill,
 * 40px height) — see root CLAUDE.md's Design source hierarchy.
 */
export const MobileDatePicker = () => {
  const translations = usePerformanceReportTranslations();
  const { dateRange, setDateRange } = usePerformanceReportContext();
  const [dateOpen, setDateOpen] = useState(false);

  // Preset ranges are derived from `new Date()`, so they're snapshotted into
  // state and refreshed each time the sheet opens (see `openSheet`). This keeps
  // ranges correct at open time — otherwise a long-mounted component could show
  // stale "Today/Last 7/Last 30" ranges and fail to match the applied preset
  // label — while keeping a stable array identity for the selector to read.
  const [datePresets, setDatePresets] = useState(() =>
    buildDatePresets(translations),
  );

  const openSheet = () => {
    setDatePresets(buildDatePresets(translations));
    setDateOpen(true);
  };

  // When the applied range matches a preset, show its label (e.g. "Last 30
  // days") instead of the formatted range — mirrors the desktop field-variant
  // `Datepicker`.
  const dateButtonLabel =
    getMatchedPresetLabel(
      datePresets,
      dateRange.startDate,
      dateRange.endDate,
    ) ?? translations.formatDateRange(dateRange.startDate, dateRange.endDate);

  return (
    <>
      <button
        type="button"
        aria-label={translations.mobileDateAriaLabel}
        onClick={openSheet}
        className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-lg bg-surface-tonal-default px-3 hover:bg-surface-tonal-bold"
      >
        <Typo
          element="span"
          type="bodySM"
          textColor="text-text-primary"
          className="min-w-0 flex-1 truncate text-start"
        >
          {dateButtonLabel}
        </Typo>
        <CalenderIcon
          className="h-4 w-4 shrink-0 text-icon-secondary"
          aria-hidden="true"
          focusable="false"
        />
      </button>

      {/* Two-step date-range flow (shortcut presets → custom picker). The
          selector owns both sheets and the step transitions; here we just
          feed it presets + the current range and commit on Apply. */}
      <MobileDateRangeSelector
        isOpen={dateOpen}
        onClose={() => setDateOpen(false)}
        onApply={(range) => {
          const clamped = clampPerformanceReportRange(range);
          setDateRange({ startDate: clamped.from, endDate: clamped.to });
          setDateOpen(false);
        }}
        value={
          dateRange.startDate && dateRange.endDate
            ? { from: dateRange.startDate, to: dateRange.endDate }
            : null
        }
        presets={datePresets}
        minDate={PERFORMANCE_REPORT_MIN_DATE}
        maxDate={new Date()}
        limitDays={PERFORMANCE_REPORT_MAX_RANGE_DAYS}
        labels={{
          title: translations.mobileDateRangeTitle,
          description: translations.dateRangeLimitation,
          customDateRange: translations.mobileCustomDateRange,
          cancel: translations.datepickerCancel,
          apply: translations.datepickerApply,
        }}
      />
    </>
  );
};
